import {
  ProblemAreaCode,
  ProblemAreaStatus,
  TrafficLightZone,
} from '../../../../api-service/clients';
import { ColorPalette } from '../../../../components/config/color-palette';
import { zoneConfig } from '../traffic-light/traffic-light-types';

interface ProblemAreaText {
  label: string;
  question: string;
  description: string;
  lookFor: string[];
  listenFor: string[];
}

// Draft texts for faglig gennemlæsning. Sources: Styrelsen for Patientsikkerhed (pixi 2021,
// https://stps.dk/Media/638251821566995226/Pixi%20Sygeplejefaglig%20journalf%c3%b8ring.pdf) and
// "De 12 sygeplejefaglige problemområder" (sundhedstjek 2022). Ordered as KL FSIII I1-I12.
export const problemAreaTexts: Record<ProblemAreaCode, ProblemAreaText> = {
  [ProblemAreaCode.FunctionalLevel]: {
    label: 'Funktionsniveau',
    question: 'Kan borgeren klare sit almindelige, daglige liv?',
    description:
      'Evnen til at klare sig selv i hverdagen, fx personlig hygiejne og påklædning. Det afhænger af, hvad der er normalt for den enkelte borger.',
    lookFor: [
      'Personlig hygiejne',
      'Af- og påklædning',
      'Vedligehold af egen bolig',
      'Aktiviteter på bostedet eller i fritiden',
      'Ændringer i forhold til det normale',
    ],
    listenFor: ['Hvordan oplever borgeren selv sit funktionsniveau?'],
  },
  [ProblemAreaCode.MusculoskeletalSystem]: {
    label: 'Bevægeapparat',
    question: 'Er der behov for træning eller problemer, fx med balancen?',
    description:
      'Aktivitet, immobilitet, lammelse, bevægelse, svimmelhed, balance, knoglebrud, støtte og faldtendens.',
    lookFor: [
      'Kan arme og ben bevæges frit?',
      'Behov for hjælpemidler eller træning',
      'Fald eller faldtendens',
      'Immobilitet kan give forstoppelse, lungebetændelse, tryksår og blærebetændelse',
    ],
    listenFor: [
      'Hvordan oplever borgeren sine evner og sin bevægelse?',
      'Er der smerter?',
    ],
  },
  [ProblemAreaCode.Nutrition]: {
    label: 'Ernæring',
    question:
      'Er borgeren over- eller undervægtig? Eller er der fx medicin, der påvirker appetitten?',
    description:
      'Kost og væskeindtag, samt om medicin påvirker appetitten. Normalt væskeindtag er 1½-2 liter om dagen.',
    lookFor: [
      'Under- eller overvægt (vægt og evt. livvidde)',
      'Småt- eller overspisende',
      'Kvalme, opkast, sure opstød eller fejlsynkning',
      'Tegn på for lidt væske: svimmelhed, træthed, mindre eller koncentreret urin',
    ],
    listenFor: [
      'Borgerens fortælling om kost og madvaner',
      'Borgerens fortælling om væskeindtag',
    ],
  },
  [ProblemAreaCode.SkinAndMucousMembranes]: {
    label: 'Hud og slimhinder',
    question: 'Har borgeren udslæt, sår eller andet?',
    description:
      'Mundpleje, tryksår, andre sår og sårkanter, fodpleje, hår og negle.',
    lookFor: [
      'Tør eller fugtig hud',
      'Sår: rødme, ømhed, varme og hævelse',
      'Ændringer i tandkød, tænder, tunge, slimhinder og læber',
    ],
    listenFor: ['Er der smerter?'],
  },
  [ProblemAreaCode.Communication]: {
    label: 'Kommunikation',
    question: 'Kan borgeren gøre sig forståelig og forstå sin omverden?',
    description:
      'Evnen til at gøre sig forståelig og forstå omverdenen. Udfordringer kan også have fysiske årsager, fx nedsat hørelse.',
    lookFor: [
      'Kommunikerer borgeren svarende til alder og evner?',
      'Søger, modtager eller undgår borgeren kontakt?',
      'Behov for hjælpemidler, fx pegeplade',
    ],
    listenFor: ['Oplever borgeren udfordringer med at kommunikere?'],
  },
  [ProblemAreaCode.PsychosocialConditions]: {
    label: 'Psykosociale forhold',
    question:
      'Er der udfordringer, fx med familie, misbrug, ensomhed eller andet?',
    description:
      'Familieforhold, relationer, ensomhed, misbrug, stemningsleje, stress og krise.',
    lookFor: [
      'Relationer til familie og andre, evt. ensomhed',
      'Grundstemning og stemningsregulering',
      'Misbrug eller tidligere misbrug',
      'Stress eller krise',
    ],
    listenFor: ['Hvordan oplever borgeren sin psykosociale situation?'],
  },
  [ProblemAreaCode.RespirationAndCirculation]: {
    label: 'Respiration og cirkulation',
    question: 'Har borgeren fx hoste, forhøjet blodtryk eller åndenød?',
    description:
      'Vejrtrækning og kredsløb. Normal vejrtrækning er 12-20 vejrtrækninger i minuttet og lydløs.',
    lookFor: [
      'Uregelmæssig eller besværet vejrtrækning',
      'Bleg, blålig eller rødblussende hud',
      'Forpustet af almindelige hverdagsting',
      'Væskeophobninger eller vægtstigning',
      'Fugtig, klam eller kold hud',
    ],
    listenFor: [
      'Hvæsen, piben, snorken eller raslen',
      'Borgerens oplevelse af sin vejrtrækning og evt. smerter',
    ],
  },
  [ProblemAreaCode.SexualityGenderAndBodyImage]: {
    label: 'Seksualitet, køn og kropsopfattelse',
    question:
      'Er der noget, der påvirker borgerens seksualitet eller kropsopfattelse?',
    description:
      'Forhold der påvirker seksualitet, fx sygdom, medicin (psykofarmaka) eller hormonbehandling.',
    lookFor: ['Påvirkning fra sygdom eller medicin'],
    listenFor: [
      'Hvilke holdninger og udfordringer har borgeren om sin seksualitet?',
    ],
  },
  [ProblemAreaCode.PainAndSensoryImpressions]: {
    label: 'Smerter og sanseindtryk',
    question: 'Har borgeren ondt? Er der problemer med syn eller hørelse?',
    description: 'Smerter, syn, hørelse og følesans.',
    lookFor: [
      'Tegn på smerte, også hos borgere uden sprog',
      'Syn, hørelse, smag og lugt',
      'Behov for hjælpemidler',
    ],
    listenFor: [
      'Hvor har borgeren ondt, og hvor stærkt (0-10)?',
      'Hvordan føles smerterne, og hvad hjælper?',
    ],
  },
  [ProblemAreaCode.SleepAndRest]: {
    label: 'Søvn og hvile',
    question: 'Sover borgeren fx dårligere end normalt?',
    description:
      'Søvn og hvile. Normalt mindst 5-6 timers sammenhængende søvn for en voksen.',
    lookFor: ['Søvnmønster og opvågninger', 'Søvn om dagen', 'Sovemedicin'],
    listenFor: [
      'Føler borgeren sig udsovet?',
      'Hvad gør borgeren, når søvnen ikke kommer?',
    ],
  },
  [ProblemAreaCode.KnowledgeAndDevelopment]: {
    label: 'Viden og udvikling',
    question: 'Forstår borgeren sin sygdom? Har borgeren brug for information?',
    description:
      'Borgerens vanlige (habituelle) tilstand, sygdomsindsigt og behov for information.',
    lookFor: [
      'Er borgeren normalt vågen og klar?',
      'Kognitive forandringer, demens eller psykisk lidelse',
      'Hvordan borgeren bedst modtager information',
    ],
    listenFor: ['Borgerens opfattelse af sit helbred og sin sygdom'],
  },
  [ProblemAreaCode.Elimination]: {
    label: 'Udskillelse af affaldsstoffer',
    question:
      'Har borgeren fx forstoppelse, diarré eller problemer med vandladningen?',
    description:
      'Vandladning og afføring. Normal vandladning er 5-6 gange i døgnet, og urinen er strågul.',
    lookFor: [
      'Urinens farve og lugt',
      'Hyppighed af vandladning og afføring',
      'Tegn på urinvejsinfektion',
      'Psykofarmaka og rusmidler kan påvirke afføringen',
    ],
    listenFor: ['Er der smerter ved vandladning eller afføring?'],
  },
};

// Dot colours: green and red reuse trafiklys, risk uses Fasit's warning amber.
const green = zoneConfig[TrafficLightZone.Green].accent;
const red = zoneConfig[TrafficLightZone.Red].accent;
// Grey dot for assessments that need no action: not relevant and closed.
const grey = '#9e9e9e';

export interface StatusPresentation {
  label: string;
  explanation: string;
  // Without a dot the status is shown as grey text.
  dot?: string;
}

export const statusPresentation: Record<ProblemAreaStatus, StatusPresentation> =
  {
    [ProblemAreaStatus.NotAssessed]: {
      label: 'Ikke vurderet',
      explanation: 'Ingen har taget stilling til området endnu.',
    },
    [ProblemAreaStatus.NoCurrentProblems]: {
      label: 'Ingen aktuelle problemer',
      explanation:
        'Området er relevant for borgeren, men der er intet at bemærke lige nu.',
      dot: green,
    },
    [ProblemAreaStatus.NotRelevant]: {
      label: 'Ikke relevant',
      explanation: 'Området har ikke betydning for borgeren.',
      dot: grey,
    },
    [ProblemAreaStatus.Risk]: {
      label: 'Risiko',
      explanation: 'Der er en risiko, som personalet skal holde øje med.',
      dot: ColorPalette.WarningAmber,
    },
    [ProblemAreaStatus.Current]: {
      label: 'Aktuel problemstilling',
      explanation: 'Der er et problem nu, som der skal handles på.',
      dot: red,
    },
  };

export const resolvedPresentation: StatusPresentation = {
  label: 'Afsluttet',
  explanation: 'Problemet er løst eller ikke længere aktuelt.',
  dot: grey,
};

export const selectableStatuses: ProblemAreaStatus[] = [
  ProblemAreaStatus.NoCurrentProblems,
  ProblemAreaStatus.NotRelevant,
  ProblemAreaStatus.Risk,
  ProblemAreaStatus.Current,
];

export function hasProblem(status: ProblemAreaStatus | undefined): boolean {
  return (
    status === ProblemAreaStatus.Risk || status === ProblemAreaStatus.Current
  );
}
